import type { MeResponse } from '@licita/contracts';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  stripSearchParams,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { createContext } from 'react';
import Shell from './components/Shell';
import { FILTROS_PADRAO, validarFiltros } from './lib/editais';
import { FITS_PADRAO, validarFiltrosFits } from './lib/fit';
import { validarBuscaPainel } from './lib/pipeline';

export const SessionContext = createContext<{ me: MeResponse | null; staging: boolean; signOut: () => void }>({
  me: null,
  staging: false,
  signOut: () => {},
});

const root = createRootRoute({ component: Shell });
const page = (path: string, load: () => Promise<{ default: React.ComponentType }>) =>
  createRoute({ getParentRoute: () => root, path, component: lazyRouteComponent(load) });

/** Lista de editais: filtros na query string (link compartilhável); só o que difere do padrão fica na URL. */
export const editaisRoute = createRoute({
  getParentRoute: () => root,
  path: '/editais',
  component: lazyRouteComponent(() => import('./pages/Editais')),
  // SearchSchemaInput: o link para /editais não precisa passar filtros (os padrões entram no validador).
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput) => validarFiltros(raw),
  search: { middlewares: [stripSearchParams(FILTROS_PADRAO)] },
});

export const editalRoute = createRoute({
  getParentRoute: () => root,
  path: '/editais/$id',
  component: lazyRouteComponent(() => import('./pages/Edital')),
});

export const clientesRoute = createRoute({
  getParentRoute: () => root,
  path: '/clientes',
  component: lazyRouteComponent(() => import('./pages/Clientes')),
});

/** `/clientes/novo` vem antes de `/clientes/$id` (rota estática vence a dinâmica). */
export const clienteNovoRoute = createRoute({
  getParentRoute: () => root,
  path: '/clientes/novo',
  component: lazyRouteComponent(() => import('./pages/Cliente'), 'ClienteNovo'),
});

export const clienteRoute = createRoute({
  getParentRoute: () => root,
  path: '/clientes/$id',
  component: lazyRouteComponent(() => import('./pages/Cliente')),
});

/** Fila de triagem dos fits: filtros na query string, como em /editais. */
export const triagemRoute = createRoute({
  getParentRoute: () => root,
  path: '/triagem',
  component: lazyRouteComponent(() => import('./pages/Triagem')),
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput) => validarFiltrosFits(raw),
  search: { middlewares: [stripSearchParams(FITS_PADRAO)] },
});

/** Painel de um fit: etapa, anexos, conversa e histórico. `?edital=` traz a ficha (não há endpoint unitário de fit). */
export const fitRoute = createRoute({
  getParentRoute: () => root,
  path: '/fits/$id',
  component: lazyRouteComponent(() => import('./pages/Fit')),
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput) => validarBuscaPainel(raw),
  search: { middlewares: [stripSearchParams({ aba: 'dados' as const })] },
});

const routeTree = root.addChildren([
  page('/', () => import('./pages/Inicio')),
  editaisRoute,
  editalRoute,
  clientesRoute,
  clienteNovoRoute,
  clienteRoute,
  triagemRoute,
  fitRoute,
  page('/sincronizacao', () => import('./pages/Sincronizacao')),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
